import { FC, useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useTranslation } from "react-i18next";
import { formatCompactNumber, formatNumber } from "../../../../common/utils";

export type PieChartItem = {
	id: string;
	label: string;
	value: number;
	percentage: number;
	measure: string;
	color: string;
};

type Props = {
	data: PieChartItem[];
	height?: number;
};

export const SimplePieChart: FC<Props> = ({ data, height = 260 }) => {
	const theme = useTheme();
	const { t } = useTranslation("common");

	// Pie charts require non negative values
	const chartData = useMemo(
		() =>
			data.map((item) => ({
				...item,
				value: Math.abs(item.value),
			})),
		[data]
	);

	const hasValues = chartData.some((item) => item.value > 0);

	const formatPercentage = (value: number) => {
		return `${formatNumber(value, "decimal", 2, 2)}%`;
	};

	const formatValue = (value: number) => {
		return formatCompactNumber(value, 2, 2);
	};

	return (
		<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
			{hasValues ? (
				<PieChart
					height={height}
					width={height}
					series={[
						{
							data: chartData,
							innerRadius: 78,
							outerRadius: 92,
							paddingAngle: 2,
							cornerRadius: 4,
							valueFormatter: (item) => {
								// Recover the original signed value for the tooltip.
								const originalItem = data.find((dataItem) => dataItem.id === item.id);

								if (!originalItem) {
									return "";
								}

								return `${formatPercentage(originalItem.percentage)} - ${formatValue(
									originalItem.value
								)} ${originalItem.measure}`;
							},
						},
					]}
					hideLegend
				/>
			) : (
				<Stack
					alignItems="center"
					justifyContent="center"
					gap={1}
					sx={{
						width: height,
						height,
					}}
				>
					<Box
						sx={{
							width: 184,
							height: 184,
							borderRadius: "50%",
							border: `14px solid ${theme.palette.grey[300]}`,
						}}
					/>

					<Typography variant="body2">{t("no data")}</Typography>
				</Stack>
			)}

			<Stack gap={0.75}>
				{data.map((item) => (
					<Stack key={item.id} direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 12,
								height: 12,
								borderRadius: 1,
								flexShrink: 0,
								backgroundColor: hasValues ? item.color : theme.palette.grey[300],
							}}
						/>

						<Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
							{formatPercentage(item.percentage)}
						</Typography>

						<Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
							{formatValue(item.value)}
						</Typography>

						<Typography variant="body2" whiteSpace="nowrap">
							{item.label}
						</Typography>
					</Stack>
				))}
			</Stack>
		</Stack>
	);
};
